/**
 * Gateway API Client
 * Authentication helpers for Gateway BFF
 */
import { ENV } from '@shared/config/env'
import { STORAGE_KEYS } from '@shared/constants'
import { createLoggerWithPrefix } from '@shared/utils/logger'
import { ENDPOINTS } from './endpoints'
import { authStore } from '@store/authStore'

const logger = createLoggerWithPrefix('gatewayClient')

export const gatewayClient = {
  /**
   * Redirect to Gateway login
   */
  login: (returnUrl: string = window.location.href): void => {
    const gatewayUrl = ENV.GATEWAY_URL
    if (!gatewayUrl) {
      logger.error('Gateway URL not configured. Set VITE_GATEWAY_URL.')
      return
    }

    const loginUrl = `${gatewayUrl}${ENDPOINTS.GATEWAY.LOGIN}?returnUrl=${encodeURIComponent(returnUrl)}`
    logger.info('Redirecting to Gateway login:', loginUrl)
    window.location.href = loginUrl
  },

  /**
   * Fetch current user from Gateway
   */
  getCurrentUser: async (): Promise<unknown | null> => {
    const gatewayUrl = ENV.GATEWAY_URL
    if (!gatewayUrl) {
      logger.error('Gateway URL not configured')
      return null
    }

    const url = `${gatewayUrl}${ENDPOINTS.GATEWAY.ME}`
    const sessionId = authStore.sessionId || localStorage.getItem(STORAGE_KEYS.SESSION_ID)

    const headers: HeadersInit = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    }

    if (sessionId) {
      headers['X-Session-Id'] = sessionId
    }

    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers,
      })

      if (!response.ok) {
        if (response.status === 401) {
          logger.warn('401 Unauthorized - session invalid')
          return null
        }
        const errorText = await response.text()
        throw new Error(`Gateway /auth/me failed: ${response.status} - ${errorText}`)
      }

      return await response.json()
    } catch (error) {
      logger.error('Fetch error:', error)
      return null
    }
  },

  /**
   * Redirect to Gateway logout
   */
  logout: (returnUrl: string = window.location.origin): void => {
    const gatewayUrl = ENV.GATEWAY_URL
    if (!gatewayUrl) {
      logger.error('Gateway URL not configured')
      return
    }

    const sessionId = authStore.sessionId || localStorage.getItem(STORAGE_KEYS.SESSION_ID)
    const params = new URLSearchParams()
    params.append('returnUrl', returnUrl)
    if (sessionId) {
      params.append('sessionId', sessionId)
    }

    const logoutUrl = `${gatewayUrl}${ENDPOINTS.GATEWAY.LOGOUT}?${params.toString()}`
    logger.info('Redirecting to Gateway logout:', logoutUrl)
    window.location.href = logoutUrl
  },

  /**
   * Extract session ID from callback URL
   */
  extractSessionIdFromUrl: (url: string = window.location.href): string | null => {
    try {
      const urlObj = new URL(url)
      return urlObj.searchParams.get('sessionId')
    } catch {
      return null
    }
  },
}
